// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — city close-up (for everyone since 2026-10-18; off with `?cityPreview=0`;
 * a preview behind `?cityPreview` from 2026-10-12). Over a region with
 * a city pack (Catalonia; the Netherlands since 2026-10-13), the globe may come down to zoom
 * {@link CITY_MAX_ZOOM} and shows 25 cm aerial photos and a 5 m terrain
 * model instead of the 500 m Blue Marble.
 *
 * The globe stays a globe (owner's choice B, 12 October 2026): MapLibre's
 * `globe` preset turns into Mercator between zoom 10 and 12, so the preview
 * uses {@link ALWAYS_SPHERE_PROJECTION} instead, which is the same
 * vertical-perspective globe at every zoom. Measured 2026-10-12 on MapLibre
 * 5.21 and 6.14 over Catalonia, with every tile loaded: clean at zoom 13 in
 * every direction and tilt up to the app's 85°, on a phone (390×844) and a
 * desktop (1440×900); from 13.5 MapLibre leaves the near part of the view
 * undrawn in some directions (on a phone, 46 % of the lower screen at 85°
 * facing north; at 14, 48 % on a desktop at 45°); from 16.6 it draws
 * nothing. With the city pack's own tiles, the zoom-11 photo underlay and the
 * 70° tilt cap (2026-10-13), zoom 14 draws sharp in most places and angles,
 * but MapLibre sometimes requests no tile for the row under the bottom edge
 * (a known fault, docs/AYNI_XR_MAPLIBRE_SPHERE_TERRAIN_PLAN.md): up to ~10 %
 * of a phone's lower screen and ~19 % of a desktop's at 30–45° then shows
 * the soft underlay or the Blue Marble. Both faults, and the terrain's
 * pillars, were traced on 2026-10-17 to MapLibre's near plane and frustum
 * for the sphere; sphereCloseupFix.ts corrects them (off with
 * `?citySphereFix=0`), which also opens {@link CITY_DEEP_MAX_ZOOM} over the
 * zoom 16–17 photos. Writing the
 * projection as the plain string
 * `'vertical-perspective'` is worse: terrain then draws nothing at any zoom.
 * Details in docs/AYNI_XR_CITY_CLOSEUP_PLAN.md.
 */

import type { ProjectionSpecification } from 'maplibre-gl'

/**
 * How close the camera may come over a city pack (owner, 2026-10-13: one
 * level more than 13, from the tiles we have): about 3.6 m a screen pixel
 * over Barcelona. Every view draws; see above for the soft near strip.
 */
export const CITY_MAX_ZOOM = 14

/**
 * How close the camera may come over a pack's zoom 16–17 photos (Barcelona,
 * Rotterdam; {@link CityPack.deep}): about 0.9 m a screen pixel. Only with
 * MapLibre's sphere corrected (sphereCloseupFix.ts, 2026-10-18); without
 * them the stock sphere draws nothing from about 16.6.
 */
export const CITY_DEEP_MAX_ZOOM = 16

/**
 * The tilt allowed close over a city pack on MapLibre's own sphere code.
 * Past 70° the near ground under a nearly level camera was clipped
 * (measured at zoom 13: 12 % of the lower screen at 85°, nothing up to 70°
 * in any direction): MapLibre's tilted frustum culling the near tiles. With
 * the sphere corrected (sphereCloseupFix.ts) the app's own limit (85°)
 * applies everywhere (owner, 2026-10-18).
 */
export const CITY_MAX_PITCH = 70
/** From this zoom over a city pack, {@link CITY_MAX_PITCH} applies. */
export const CITY_PITCH_FROM_ZOOM = 11

/**
 * The tilt allowed at a zoom, over a city pack or not; `elsewhere` is the
 * app's own limit, which also applies over a pack once the sphere is corrected.
 */
export function cityMaxPitch(overPack: boolean, zoom: number, elsewhere: number, sphereFixed = false): number {
  return overPack && !sphereFixed && zoom >= CITY_PITCH_FROM_ZOOM ? Math.min(elsewhere, CITY_MAX_PITCH) : elsewhere
}

/**
 * The vertical-perspective globe at every zoom. An expression (not the
 * plain string) so MapLibre uses its globe transform, which drapes terrain;
 * the second stop is past any zoom the app allows, so it never applies.
 */
export const ALWAYS_SPHERE_PROJECTION: ProjectionSpecification = {
  type: ['step', ['zoom'], 'vertical-perspective', 30, 'vertical-perspective'],
}

/**
 * A region whose close-up tiles exist in AYNI's bucket `ayni-basemaps`
 * (served by /api/map/city/<id>/…; the bucket's `city/index.json` names the
 * files), as a lon/lat box with the credit lines Tools → Credits shows.
 */
export interface CityPack {
  id: string
  /** West, south, east, north in degrees: also the tile sources' `bounds`. */
  bounds: [number, number, number, number]
  photoCredit: string
  /** The terrain's credit, with the notices its licences require. */
  terrainCredit: string
  /** The terrain file's deepest level (the index's `terrain.maxzoom`). */
  terrainMaxZoom: number
  /**
   * The box with photos at zoom 16–17 (about 1 m a pixel; the rest of the
   * pack stops at 15), and its deepest level.
   */
  deep?: { bounds: [number, number, number, number]; maxzoom: number }
}

/**
 * Catalonia (box with a small margin): ICGC's 2025 25 cm photos, and terrain
 * from ICGC's 2021–2023 LiDAR model filled out with Copernicus WorldDEM-30,
 * built by the VPS on 2026-10-12. The credits are the pack index's, and the
 * Copernicus licence asks for its no-liability notice beside them.
 */
export const CATALONIA: CityPack = {
  id: 'catalonia',
  bounds: [0.15, 40.5, 3.35, 42.9],
  photoCredit: 'Aerial photo: Institut Cartogràfic i Geològic de Catalunya (ICGC), CC BY 4.0, ortofoto de Catalunya 25 cm 2025',
  terrainCredit: "Terrain: Institut Cartogràfic i Geològic de Catalunya (ICGC), CC BY 4.0, model d'elevacions del terreny LiDAR 2021-2023 (modified by AYNI); produced using Copernicus WorldDEM-30 © DLR e.V. 2010-2014 and © Airbus Defence and Space GmbH 2014-2018 provided under COPERNICUS by the European Union and ESA; all rights reserved. The organisations in charge of the Copernicus programme by law or by delegation do not incur any liability for any use of the Copernicus WorldDEM-30.",
  terrainMaxZoom: 14,
  // Barcelona's metropolitan area (handoff-vps-city-tiles.md; tiles checked live 2026-10-18).
  deep: { bounds: [1.9, 41.25, 2.35, 41.55], maxzoom: 17 },
}

/**
 * The Netherlands (box with a small margin, Wadden Islands included):
 * Beeldmateriaal Nederland's 2025 25 cm photos (flown 2025-04-27 to
 * 2025-06-21), and AHN4's 5 m ground model (CC0) with water, buildings'
 * holes and the land across the border filled from Copernicus GLO-30, built
 * by the VPS on 2026-10-13. Flat country: terrain only to zoom 11.
 */
export const NETHERLANDS: CityPack = {
  id: 'netherlands',
  bounds: [3.3, 50.7, 7.25, 53.6],
  photoCredit: 'Aerial photo: Beeldmateriaal Nederland (beeldmateriaal.nl), CC BY 4.0, luchtfoto 25 cm 2025',
  terrainCredit: 'Terrain: Actueel Hoogtebestand Nederland (AHN4), CC0 (modified by AYNI); produced using Copernicus WorldDEM-30 © DLR e.V. 2010-2014 and © Airbus Defence and Space GmbH 2014-2018 provided under COPERNICUS by the European Union and ESA; all rights reserved. The organisations in charge of the Copernicus programme by law or by delegation do not incur any liability for any use of the Copernicus WorldDEM-30.',
  terrainMaxZoom: 11,
  // Rotterdam with its port and the Maasvlakte (handoff-vps-netherlands-tiles.md; checked live 2026-10-18).
  deep: { bounds: [4.0, 51.8, 4.65, 52.05], maxzoom: 17 },
}

export const CITY_PACKS: readonly CityPack[] = [CATALONIA, NETHERLANDS]

/**
 * Whether the city close-up is on: for everyone since 2026-10-18 (owner),
 * when MapLibre's sphere was corrected (sphereCloseupFix.ts). A page opened
 * with `?cityPreview=0` gets the plain globe preset and the old zoom-8
 * ceiling instead (the switch kept its preview-era name so links still work).
 */
export function isCityCloseup(search: string = globalThis.location?.search ?? ''): boolean {
  try {
    return new URLSearchParams(search).get('cityPreview') !== '0'
  } catch {
    return true
  }
}

/**
 * False when the page was opened with `?citySphereFix=0`: the city close-up
 * then runs on MapLibre's own sphere code (sphereCloseupFix.ts), for
 * comparing and as a way out if the corrections misbehave on a device.
 */
export function isCitySphereFix(search: string = globalThis.location?.search ?? ''): boolean {
  try {
    return new URLSearchParams(search).get('citySphereFix') !== '0'
  } catch {
    return true
  }
}

/** True when a point lies in a pack's zoom 16–17 photo box. */
export function inDeepBox(pack: CityPack | null, lng: number, lat: number): boolean {
  const box = pack?.deep?.bounds
  return !!box && lng >= box[0] && lng <= box[2] && lat >= box[1] && lat <= box[3]
}

/** The pack covering a point, or null. */
export function cityPackAt(lng: number, lat: number): CityPack | null {
  return CITY_PACKS.find(({ bounds: [w, s, e, n] }) => lng >= w && lng <= e && lat >= s && lat <= n) ?? null
}

/**
 * From this zoom the terrain is not drawn at all (owner, 2026-10-13).
 * MapLibre's terrain on the always-sphere globe draws thin vertical
 * "pillars" across the city from zoom ~12.5: not from the heights (they
 * show at 0.0001× exaggeration too, and the tiles decode clean), not at
 * tile edges, and not with the plain globe preset. A MapLibre bug, planned
 * upstream (docs/AYNI_XR_MAPLIBRE_SPHERE_TERRAIN_PLAN.md).
 */
export const CITY_TERRAIN_OFF_ZOOM = 12

/**
 * Terrain exaggeration for the camera's zoom: the whole-globe 5× far out,
 * real heights (1×) from zoom 10 to 11, then flattening to 0 at
 * {@link CITY_TERRAIN_OFF_ZOOM}, where the terrain is switched off, so the
 * relief settles instead of popping. With MapLibre's sphere corrected
 * (`sphereFixed`, sphereCloseupFix.ts: no more pillars) real heights stay
 * on all the way in.
 */
export function cityTerrainExaggeration(zoom: number, sphereFixed = false): number {
  if (sphereFixed && zoom >= 10) return 1
  if (zoom >= CITY_TERRAIN_OFF_ZOOM) return 0
  if (zoom >= CITY_TERRAIN_OFF_ZOOM - 1) return CITY_TERRAIN_OFF_ZOOM - zoom
  const t = Math.max(0, Math.min(1, (zoom - 8) / 2))
  return 5 + (1 - 5) * t
}
